import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/ui/PhoneInput';
import { Loader2, CheckCircle, MessageSquare } from 'lucide-react';
import { getWeaveinApiUrl, config } from '@/src/config';

interface FormMeta {
  slug: string;
  organization_name: string;
  logo_url: string | null;
  program_name: string;
  consent_disclosure: string;
  privacy_policy_url: string;
  terms_url: string;
}

/**
 * Public, unauthenticated SMS opt-in form (A2P 10DLC compliance).
 * Renders per-organization by slug; the backend gates on
 * cbo.sms_opt_in_enabled and returns the exact consent disclosure to display
 * (and store as the consent snapshot on submit).
 */
export default function SmsOptInPage({ slug }: { slug: string }) {
  const [meta, setMeta] = useState<FormMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(getWeaveinApiUrl(`/api/public/sms-opt-in/${encodeURIComponent(slug)}`));
        if (!active) return;
        if (res.ok) {
          setMeta(await res.json());
        } else {
          setNotFound(true);
        }
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  const logo = meta?.logo_url || (config.isWeavein ? '/weavin-logo.png' : '/triveny/logo.svg');
  const privacyUrl = meta?.privacy_policy_url || 'https://weavein.ai/privacy-policy.html';
  const termsUrl = meta?.terms_url || 'https://weavein.ai/terms-of-use.html';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!consent) {
      setError('Please check the box to consent to receive text messages.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/public/sms-opt-in/${encodeURIComponent(slug)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName, last_name: lastName, phone, consent }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setDone(true);
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="WeaveIn" className="h-10 w-auto" />
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      </Shell>
    );
  }

  if (notFound || !meta) {
    return (
      <Shell>
        <Card>
          <CardContent className="py-10 text-center">
            <h1 className="text-lg font-semibold text-gray-900">Sign-up unavailable</h1>
            <p className="mt-2 text-sm text-gray-600">
              This text-message sign-up is not currently available. Please contact your care organization.
            </p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-teal-600 mx-auto" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900">You're subscribed!</h1>
            <p className="mt-2 text-sm text-gray-600">
              You'll now receive {meta.program_name} text messages from {meta.organization_name}. Reply{' '}
              <strong>STOP</strong> at any time to opt out, or <strong>HELP</strong> for help.
            </p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3">
            <MessageSquare className="w-6 h-6 text-teal-700" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">{meta.program_name}</h1>
          <p className="text-sm text-gray-600 mt-1">
            Get text updates from {meta.organization_name}.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                placeholder="(555) 555-5555"
                autoComplete="tel"
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              />
            </div>

            <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-xs leading-relaxed text-gray-600">{meta.consent_disclosure}</span>
            </label>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-v2accent hover:bg-v2accent/90 text-white"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign up for text updates'}
            </Button>

            <p className="text-center text-xs text-gray-400">
              <a href={privacyUrl} className="underline hover:text-gray-600" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              {' · '}
              <a href={termsUrl} className="underline hover:text-gray-600" target="_blank" rel="noopener noreferrer">
                Text Messaging Terms
              </a>
            </p>
          </form>
        </CardContent>
      </Card>
    </Shell>
  );
}
